import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ContentPageModel } from "@solveindustrial/Services/ContentSearchService";
import addContentFilters from "@solveindustrial/Store/Handlers/ContentSearch/AddContentFilters";
import { CustomState } from "@solveindustrial/Store/Reducers";
import { mediaQueriesBP } from "@solveindustrial/Utilities/variables";
import ContentSearchCard from "@solveindustrial/Widgets/ContentSearch/ContentSearchCard";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showTitle = "showTitle",
    showDescription = "showDescription",
    showDate = "showDate",
    maxDescriptionLength = "maxDescriptionLength",
    showImage = "showImage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showTitle]: boolean;
        [fields.showDescription]: boolean;
        [fields.showDate]: boolean;
        [fields.maxDescriptionLength]: number;
        [fields.showImage]: boolean;
    };
}

const mapStateToProps = (state: CustomState) => {
    const {
        isLoading,
        searchResults,
        pagination,
        contentFilters: { query },
    } = state.contentSearch;

    const searchResultsCount = searchResults && pagination ? pagination.totalItemCount : 0;

    return {
        isLoading,
        contentPages: searchResults,
        pagination,
        query,
        searchResultsCount,
    };
};

const mapDispatchToProps = {
    addContentFilters,
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface ContentSearchCardListStyles {
    wrapper?: InjectableCss;
    cardList?: CardListStyles;
    noResultsText?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    cardContainerStyles?: CardContainerStyles;
    pageTitle?: TypographyPresentationProps;
    pageDescription?: TypographyPresentationProps;
    pageType?: TypographyPresentationProps;
    pageDate?: TypographyPresentationProps;
    pageLink?: LinkPresentationProps;
    loadMoreWrapper?: InjectableCss;
    loadMoreButton?: ButtonPresentationProps;
    pagination?: PaginationPresentationProps;
    paginationWrapper?: InjectableCss;
    paginationText?: TypographyPresentationProps;
}

export const contentSearchCardListStyles: ContentSearchCardListStyles = {
    cardList: {
        gridContainer: {
            gap: 0,
            css: css`
                border-top: 1px solid ${getColor("common.border")};
            `,
        },
    },
    centeringWrapper: {
        css: css`
            width: 100%;
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsText: {
        variant: "h4",
        css: css`
            display: block;
            margin: auto;
        `,
    },
    cardContainerStyles: {
        cardDivider: {
            css: css`
                width: 100%;
                border: none;
                border-bottom: 1px solid ${getColor("common.border")};
            `,
        },
    },
    pageTitle: {
        variant: "h3",
        css: css`
            margin-bottom: 10px;
        `,
    },
    pageDescription: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    pageType: {
        color: "secondary",
        size: 14,
        css: css`
            margin-bottom: 5px;
        `,
    },
    pageDate: {
        color: "text.secondary",
        size: 12,
        css: css`
            margin-top: 10px;
        `,
    },
    pageLink: {
        css: css`
            margin-top: 15px;
            display: inline-block;
        `,
    },
    loadMoreWrapper: {
        css: css`
            width: 100%;
            height: 100px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    loadMoreButton: {
        variant: "secondary",
        css: css`
            margin: 0 10px;
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
    paginationWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            margin: 20px 0;
            width: 100%;
            gap: 10px;

            ${mediaQueriesBP.minMD} {
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
            }
        `,
    },
    paginationText: {
        variant: "h4",
        css: css`
            margin: 0;
        `,
    },
};

const styles = contentSearchCardListStyles;

const ContentSearchCardList = ({
    id,
    isLoading,
    contentPages,
    pagination,
    query,
    searchResultsCount,
    fields,
    addContentFilters,
}: Props) => {
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);

    if (isLoading || !contentPages.length) {
        return !isLoading && searchResultsCount === 0 ? (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.centeringWrapper} data-test-selector="contentSearchNoneFound">
                    <Zone contentId={id} zoneName="Content01" />
                </StyledWrapper>
            </StyledWrapper>
        ) : (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} data-test-selector="contentSearchCardListSpinner" />
            </StyledWrapper>
        );
    }

    const renderContentPages = () => {
        return contentPages.map((page: ContentPageModel) => (
            <CardContainer key={page.id} extendedStyles={styles.cardContainerStyles}>
                <ContentSearchCard
                    page={page}
                    showTitle={fields.showTitle}
                    showDescription={fields.showDescription}
                    showDate={fields.showDate}
                    maxDescriptionLength={fields.maxDescriptionLength}
                    showImage={fields.showImage}
                />
            </CardContainer>
        ));
    };

    const changePage = (newPageIndex: number) => {
        addContentFilters({ page: newPageIndex });
        setShouldScrollToTop(true);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        addContentFilters({ pageSize: newPageSize });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <LoadingOverlay loading={isLoading}>
                {searchResultsCount > 0 && pagination && (
                    <StyledWrapper {...styles.paginationWrapper}>
                        <Typography {...styles.paginationText}>
                            {searchResultsCount} {translate("results")}
                        </Typography>
                        <Pagination
                            {...styles.pagination}
                            currentPage={pagination.page}
                            resultsPerPage={pagination.pageSize}
                            resultsCount={pagination.totalItemCount}
                            resultsPerPageOptions={pagination.pageSizeOptions}
                            onChangePage={changePage}
                            onChangeResultsPerPage={changeResultsPerPage}
                            prevPage={pagination.prevPageUri}
                            nextPage={pagination.nextPageUri}
                        />
                    </StyledWrapper>
                )}
                <CardList extendedStyles={styles.cardList} data-test-selector="contentSearchCardList">
                    {renderContentPages()}
                </CardList>
                {contentPages.length > 0 && pagination && (
                    <Pagination
                        {...styles.pagination}
                        currentPage={pagination.currentPage}
                        resultsPerPage={pagination.pageSize}
                        resultsCount={pagination.totalItemCount}
                        resultsPerPageOptions={pagination.pageSizeOptions}
                        onChangePage={changePage}
                        onChangeResultsPerPage={changeResultsPerPage}
                        prevPage={pagination.prevPageUri}
                        nextPage={pagination.nextPageUri}
                    />
                )}
                {isLoading && (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner} />
                    </StyledWrapper>
                )}
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ContentSearchCardList),
    definition: {
        displayName: "Content Search Results",
        allowedContexts: ["ContentSearchPage"],
        group: "Products",
        fieldDefinitions: [
            {
                name: fields.showTitle,
                displayName: "Show Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showDescription,
                displayName: "Show Description",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showDate,
                displayName: "Show Publish Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.maxDescriptionLength,
                displayName: "Maximum Description Length",
                editorTemplate: "IntegerField",
                defaultValue: 200,
                fieldType: "General",
                sortOrder: 5,
                isVisible: widget => widget.fields.showDescription,
            },
            {
                name: fields.showImage,
                displayName: "Show Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
        ],
    },
};

export default widgetModule;
